import React, { useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Hero from '../components/services/Hero';
import ServiceSlide from '../components/services/ServiceSlide';

const Services: React.FC = () => {
  const { t } = useTranslation();

  // Service image arrays
  const internationalFreightImages = [
    '/images/Service/service-slide/1.0.jpg',
    '/images/Service/service-slide/1.1.jpg', 
    '/images/Service/service-slide/1.2.jpg',
    '/images/Service/service-slide/1.3.jpg',
    '/images/Service/service-slide/1.4.jpg'
  ];

  const heavyCargoImages = [
    '/images/Service/service-slide/2.0.jpg',
    '/images/Service/service-slide/2.1.jpg', 
    '/images/Service/service-slide/2.2.jpg',
    '/images/Service/service-slide/2.3.jpg',
    '/images/Service/service-slide/2.4.jpg'
  ];

  const warehousesImages = [
    '/images/Service/service-slide/3.0.jpg',
    '/images/Service/service-slide/3.1.JPG', 
    '/images/Service/service-slide/3.2.jpg',
    '/images/Service/service-slide/3.3.JPG',
    '/images/Service/service-slide/3.4.jpg'
  ];

  const customsClearanceImages = [
    '/images/Service/service-slide/4.0.jpg',
    '/images/Service/service-slide/4.1.jpg', 
    '/images/Service/service-slide/4.2.jpg',
    '/images/Service/service-slide/4.3.jpg',
    '/images/Service/service-slide/4.4.JPG'
  ];

  const packagingImages = [
    '/images/Service/service-slide/5.0.png',
    '/images/Service/service-slide/5.1.jpg', 
    '/images/Service/service-slide/5.2.jpg',
    '/images/Service/service-slide/5.3.jpg',
    '/images/Service/service-slide/5.4.jpg'
  ];

  useEffect(() => {
    // Get the hash from the URL
    const hash = window.location.hash;
    
    if (hash) {
      // Remove the # from the hash
      const elementId = hash.substring(1);
      // Find the element
      const element = document.getElementById(elementId);
      
      if (element) {
        // Scroll the element into view with smooth behavior
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []); // Empty dependency array means this runs once when component mounts

  return (
    <>
      <Hero />
      <div className="services-page pt-3 margin-top-75">
        <Container>
          {/* International Air/Sea Freight Section */}
          <div id="international-freight" className="service-section mb-5">
            <Row>
              <Col md={7}>
                <h2 className="header-2 color-brown service-title">{t('services.internationalFreight.title')}</h2>
                <h2 className="header-2 color-brown mb-4 service-subtitle">{t('services.internationalFreight.subtitle')}</h2>
                <p className="service-description mb-2">
                  {t('services.internationalFreight.description-1')} <span className='menu-item bold'>{t('services.internationalFreight.company')}</span> {t('services.internationalFreight.description-2')}
                </p>
                <div className="service-list-container d-flex">
                  <ul className="service-list service-list-international-freight">
                    <li>{t('services.internationalFreight.locations.airport')}</li>
                    <li>{t('services.internationalFreight.locations.port')}</li>
                    <li>{t('services.internationalFreight.locations.sihanoukville')}</li>
                  </ul>
                  <ul className="service-list ms-5 service-list-international-freight">
                    <li>{t('services.internationalFreight.locations.poipet')}</li>
                    <li>{t('services.internationalFreight.locations.bavet')}</li>
                  </ul>
                </div>
              </Col>
              <Col md={5}>
                <ServiceSlide 
                  images={internationalFreightImages}
                  alt={t('services.internationalFreight.title')}
                  className="service-image image-hover-animation w-100"
                  style={{ height: '350px' }}
                />
              </Col>
            </Row>
          </div>

          {/* Heavy and Project Cargo Section */}
          <div id="heavy-cargo" className="service-section mb-5">
            <Row>
              <Col md={7}>
                <h2 className="header-2 color-brown service-title pt-1">{t('services.heavyCargo.title')}</h2>
                <p className="service-description mb-2">
                  {t('services.heavyCargo.description')}
                </p>
                <div className="service-list-container d-flex">
                  <ul className="service-list">
                    <li>{t('services.heavyCargo.items.machinery')}</li>
                    <li>{t('services.heavyCargo.items.turbines')}</li>
                  </ul>
                  <ul className="service-list ms-5">
                    <li>{t('services.heavyCargo.items.equipment')}</li>
                    <li>{t('services.heavyCargo.items.etc')}</li>
                  </ul>
                </div>
              </Col>
              <Col md={5}>
                <ServiceSlide 
                  images={heavyCargoImages}
                  alt={t('services.heavyCargo.title')}
                  className="service-image image-hover-animation w-100"
                  style={{ height: '350px' }}
                />
              </Col>
            </Row>
          </div>

          {/* Warehouses Section */}
          <div id="warehouse" className="service-section mb-5">
            <Row>
              <Col md={5}>
                <ServiceSlide 
                  images={warehousesImages}
                  alt={t('services.warehouses.title')}
                  className="service-image image-hover-animation w-100"
                  style={{ height: '350px' }}
                />
              </Col>
              <Col md={7}>
                <h2 className="header-2 color-brown service-title pt-1">{t('services.warehouses.title')}</h2>
                <p className="service-description mb-2"> <span className='menu-item bold'>{t('services.internationalFreight.company')}</span>
                  {t('services.warehouses.description')}
                </p>
                <div className="service-list-container d-flex">
                  <ul className="service-list">
                    <li>{t('services.warehouses.services.distribution')}</li>
                    <li>{t('services.warehouses.services.bonded')}</li>
                  </ul>
                  <ul className="service-list ms-5">
                    <li>{t('services.warehouses.services.climate')}</li>
                    <li>{t('services.warehouses.services.etc')}</li>
                  </ul>
                </div>
              </Col>
            </Row>
          </div>

          {/* Customs Clearance Section */}
          <div id="customs-clearance" className="service-section mb-5">
            <Row>
              <Col md={5}>
                <ServiceSlide 
                  images={customsClearanceImages}
                  alt={t('services.customsClearance.title')}
                  className="service-image image-hover-animation w-100"
                  style={{ height: '350px' }}
                />
              </Col>
              <Col md={7}>
                <h2 className="header-2 color-brown service-title pt-1">{t('services.customsClearance.title')}</h2>
                <h2 className="header-2 color-brown mb-4">{t('services.customsClearance.subtitle')}</h2>
                <p className="service-description mb-2">
                  {t('services.customsClearance.description')}
                </p>
              </Col>
            </Row>
          </div>

          {/* Door to Door Services Section */}
          <div id="packaging" className="service-section mb-2">
            <Row>
              <Col md={7}>
                <h2 className="header-2 color-brown service-title pt-1">{t('services.packaging.title')}</h2>
                <p className="service-description mb-4">
                  {t('services.packaging.description-1')}<span className='menu-item bold'>{t('services.internationalFreight.company')}</span>{t('services.packaging.description-2')}
                </p>
              </Col>
              <Col md={5}>
                <ServiceSlide 
                  images={packagingImages}
                  alt={t('services.packaging.title')}
                  className="service-image image-hover-animation w-100"
                  style={{ height: '350px' }}
                />
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </>
  );
};

export default Services; 